import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import callBackendApi from '@/apis/call';
import { ElectionRecord, getElectionsContaining } from '@/apis/elections';
import {
  ElectionRecordResult,
  Supporter,
  TASK_CHOICE_MAPPING_REVERSE,
  TASK_KIND_MAPPING_REVERSE,
  TaskChoice,
  TaskKind,
  isPermissibleTaskChoiceAndKind,
} from '@/types';
import { labelSorting } from '@/util/labelSorting';
import { GroupRecordResult } from '@components/GroupsFetchAutocomplete';
import { TagRecordResult } from '@components/TagsFetchAutocomplete';
import { useDistributionListAddressCount } from '@components/DistributionListPanel';
import { useOtherActionsPanelFormHandlers } from '@components/OtherActionsPanelContent/shared/useOtherActionsPanelFormHandlers';

export type UseOtherActionsPanelFormParams = {
  accordionStartingState: boolean[];
  listId: number;
  sortColumn: string;
  sortOrder: string;
  contactIds?: number[];
  selectedContacts?: Supporter[];
};

export function useOtherActionsPanelForm({
  accordionStartingState: ACCORDION_STARTING_STATE,
  listId,
  sortColumn,
  sortOrder,
  contactIds,
  selectedContacts,
}: UseOtherActionsPanelFormParams) {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);

  const [accordionExpansion, setAccordionExpansion] = useState<boolean[]>(ACCORDION_STARTING_STATE);
  const [groupAddEntities, setGroupAddEntities] = useState<GroupRecordResult[]>([]);
  const [groupRemoveEntities, setGroupRemoveEntities] = useState<GroupRecordResult[]>([]);
  const [newOrExistingGroup, setNewOrExistingGroup] = useState<'existing' | 'new'>('existing');
  const [newGroupNames, setNewGroupNames] = useState<GroupRecordResult[]>([]);
  const [groupOverlap, setGroupOverlap] = useState(false);
  const [groupNamesClash, setGroupNamesClash] = useState<string[]>([]);
  const [tagAddEntities, setTagAddEntities] = useState<TagRecordResult[]>([]);
  const [tagRemoveEntities, setTagRemoveEntities] = useState<TagRecordResult[]>([]);
  const [tagOverlap, setTagOverlap] = useState(false);
  const [updateRocketStatus, setUpdateRocketStatus] = useState<string | null>(null);
  const [updatePathwayStep, setUpdatePathwayStep] = useState<{ label: string; id: number } | null>(
    null
  );
  const [updatePathwayOption, setUpdatePathwayOption] = useState<{
    label: string;
    id: number;
  } | null>(null);
  const [electionValue, setElectionValue] = useState<ElectionRecordResult | null>(null);
  const [levelOfInterest, setLevelOfInterest] = useState<string | null>(null);
  const [taskKindType, setTaskKindType] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [isDownloadPdfChecked, setIsDownloadPdfChecked] = useState(false);
  const [deleteFromListChecked, setDeleteFromListChecked] = useState(false);

  const { data: upcomingElection } = useQuery({
    queryKey: ['upcomingElection', domainCode],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return callBackendApi({
        endpoint: `/elections/upcoming?domainCode=${domainCode}`,
        method: 'get',
        token,
      }) as Promise<ElectionRecord>;
    },
    enabled: false,
  });

  const [distributionListNotes, setDistributionListNotes] = useState('');
  const [distributionListElection, setDistributionListElection] = useState<{
    id: number;
    label: string;
    shortName: string;
  }>({
    id: upcomingElection?.id ?? -1,
    shortName: upcomingElection?.shortname ?? '',
    label: upcomingElection?.name ?? '',
  });

  const hasDeleteFromListSelection = (selectedContacts?.length ?? 0) > 0;
  const deleteFromListResetKey = hasDeleteFromListSelection ? 'with-selection' : 'no-selection';

  const prevDeleteFromListResetKeyRef = useRef(deleteFromListResetKey);
  // eslint-disable-next-line react-hooks/refs -- reset checkbox when selection presence changes
  if (prevDeleteFromListResetKeyRef.current !== deleteFromListResetKey) {
    // eslint-disable-next-line react-hooks/refs -- store latest reset key
    prevDeleteFromListResetKeyRef.current = deleteFromListResetKey;
    setDeleteFromListChecked(false);
  }

  const canDeleteFromList = useMemo(() => {
    if (!deleteFromListChecked) return false;
    if (selectedContacts === undefined) return false;
    return (
      selectedContacts.filter((contact) => !['Uncalled', 'Skipped'].includes(contact.callingStatus))
        .length === 0
    );
  }, [deleteFromListChecked, selectedContacts]);

  const { data: distributionAddressCountData, isFetching: isFetchingDistributionAddressCount } =
    useDistributionListAddressCount({
      listId,
      sortColumn,
      sortOrder,
      contactIds,
    });

  const isDownloadPdfCount = useMemo(
    () => (isFetchingDistributionAddressCount ? 0 : (distributionAddressCountData ?? 0)),
    [isFetchingDistributionAddressCount, distributionAddressCountData]
  );

  const taskKindCode = taskKindType ? TASK_KIND_MAPPING_REVERSE[taskKindType] : null;
  const taskChoiceCode = levelOfInterest ? TASK_CHOICE_MAPPING_REVERSE[levelOfInterest] : null;

  const taskStatusError =
    taskKindType && levelOfInterest
      ? !isPermissibleTaskChoiceAndKind(taskChoiceCode as TaskChoice, taskKindCode as TaskKind)
      : false;
  const taskStatusHelperText = taskStatusError ? 'Task status invalid for selected task type' : '';

  const lookupByElectionName = async (lookupValue: string) => {
    const token = await getAccessTokenSilently();
    const returnData = await getElectionsContaining(token, lookupValue, domainCode);
    return returnData
      .map((x) => ({ id: x.id, label: x.name }))
      .sort(labelSorting);
  };

  const isAtLeastOneFieldSet =
    (groupAddEntities.length ||
      groupRemoveEntities.length ||
      newGroupNames.length ||
      tagAddEntities.length ||
      tagRemoveEntities.length ||
      updateRocketStatus !== null ||
      (electionValue !== null && taskKindCode != null && taskChoiceCode != null) ||
      (updatePathwayStep != null && updatePathwayOption != null) ||
      (isDownloadPdfChecked && isDownloadPdfCount > 0) ||
      canDeleteFromList) &&
    !taskStatusError;

  const handlers = useOtherActionsPanelFormHandlers({
    accordionStartingState: ACCORDION_STARTING_STATE,
    groupAddEntities,
    setGroupAddEntities,
    groupRemoveEntities,
    setGroupRemoveEntities,
    setNewOrExistingGroup,
    newGroupNames,
    setNewGroupNames,
    setGroupOverlap,
    groupNamesClash,
    setGroupNamesClash,
    tagAddEntities,
    setTagAddEntities,
    tagRemoveEntities,
    setTagRemoveEntities,
    setTagOverlap,
    setAccordionExpansion,
    setUpdateRocketStatus,
    setElectionValue,
    setTaskKindType,
    setLevelOfInterest,
    setNotes,
    setUpdatePathwayStep,
    setUpdatePathwayOption,
  });

  const applyDisabled =
    !isAtLeastOneFieldSet ||
    groupOverlap ||
    tagOverlap ||
    (newOrExistingGroup === 'new' &&
      (groupNamesClash.length > 0 ||
        newGroupNames.filter((x) => x.label.length < 3).length > 0));

  return {
    accordionExpansion,
    setAccordionExpansion,
    groupAddEntities,
    groupRemoveEntities,
    newOrExistingGroup,
    newGroupNames,
    groupOverlap,
    groupNamesClash,
    tagAddEntities,
    tagRemoveEntities,
    tagOverlap,
    updateRocketStatus,
    setUpdateRocketStatus,
    updatePathwayStep,
    setUpdatePathwayStep,
    updatePathwayOption,
    setUpdatePathwayOption,
    electionValue,
    levelOfInterest,
    taskKindType,
    setTaskKindType,
    setLevelOfInterest,
    notes,
    isDownloadPdfChecked,
    setIsDownloadPdfChecked,
    deleteFromListChecked,
    setDeleteFromListChecked,
    distributionListNotes,
    setDistributionListNotes,
    distributionListElection,
    setDistributionListElection,
    canDeleteFromList,
    isAtLeastOneFieldSet,
    taskStatusError,
    taskStatusHelperText,
    lookupByElectionName,
    applyDisabled,
    ...handlers,
  };
}

export type OtherActionsPanelFormState = ReturnType<typeof useOtherActionsPanelForm>;
